import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { appPath } from '../../appConfig.ts'
import { getFeeder } from '../../api/topology.ts'
import { usePhaseBalance, useVoltageDrop } from '../../hooks/useEngineering.ts'

interface FeederDetailProps {
  id: number
  onNavigate: (type: string, id: number) => void
}

export function FeederDetail({ id, onNavigate }: FeederDetailProps) {
  const { data: feeder, isLoading } = useQuery({
    queryKey: ['feeder', id],
    queryFn: () => getFeeder(id),
  })

  const { data: phaseBalance } = usePhaseBalance(id)
  const { data: voltageDrop } = useVoltageDrop(id)

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading feeder...</div>
  }

  if (!feeder) {
    return <div className="p-4 text-sm text-red-400">Feeder not found</div>
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="text-base font-semibold text-gray-900">
          {feeder.name || feeder.code || `Feeder #${feeder.id}`}
        </div>
        {feeder.code && feeder.name && (
          <div className="text-sm text-gray-500">Code: {feeder.code}</div>
        )}
      </div>

      {/* Key details */}
      <div className="space-y-1.5 text-sm">
        <DetailRow label="GIS ID" value={feeder.gis_id} />
        <DetailRow label="Phase" value={feeder.phase} />
        <DetailRow label="Voltage (kV)" value={feeder.voltage_kv} />
        <DetailRow label="Transformers" value={feeder.transformer_count} />
      </div>

      {/* Parent substation */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Parent Substation</p>
        {feeder.substation ? (
          <button
            onClick={() => onNavigate('Substation', feeder.substation!.id)}
            className="w-full text-left p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-sm font-medium text-gray-700">
                {feeder.substation.name || feeder.substation.code || `Substation #${feeder.substation.id}`}
              </span>
            </div>
          </button>
        ) : (
          <div className="p-2 rounded bg-orange-50 border border-orange-200 text-sm text-orange-700">
            No substation linked
          </div>
        )}
      </div>

      {/* Engineering preview */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Engineering</p>
        <div className="space-y-1.5 text-sm">
          {phaseBalance && (
            <div className="flex justify-between">
              <span className="text-gray-500">Phase Balance</span>
              <span
                className={`font-medium ${
                  phaseBalance.is_balanced === null
                    ? 'text-gray-500'
                    : phaseBalance.is_balanced
                      ? 'text-green-600'
                      : 'text-orange-600'
                }`}
              >
                {phaseBalance.is_balanced === null
                  ? 'Phase identity unknown'
                  : `${phaseBalance.is_balanced ? 'Balanced' : 'Imbalanced'} (${phaseBalance.imbalance_pct}%)`}
              </span>
            </div>
          )}
          {voltageDrop && voltageDrop.total_segments > 0 && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-500">Max Voltage Drop</span>
                <span className={`font-medium ${voltageDrop.max_drop_pct > 5 ? 'text-red-600' : voltageDrop.max_drop_pct > 3 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {voltageDrop.max_drop_pct.toFixed(2)}%
                </span>
              </div>
              {voltageDrop.violation_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Violations</span>
                  <span className="font-medium text-red-600">{voltageDrop.violation_count}</span>
                </div>
              )}
            </>
          )}
        </div>
        <Link
          to={appPath('/engineering')}
          className="mt-2 block text-xs text-blue-600 hover:text-blue-800"
        >
          View in Engineering Dashboard →
        </Link>
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
